import {
  type DocumentData,
  type Firestore,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { z } from 'zod';

/**
 * LE BRANDING D'UN CLIENT : le nom de son environnement, son logo, son icone et
 * sa couleur d'accent, choisis depuis Parametres et ranges dans UN SEUL document,
 * `orgs/{orgId}/settings/branding`. La regle qui fait foi vit dans
 * `firebase/firestore.rules`, bloc `orgs/{orgId}/settings/branding`.
 *
 * CE N'EST PAS UNE FAMILLE, et presque tout ce qui suit en decoule.
 *
 * 1. SON SCHEMA ZOD VIT ICI, pas dans `@taki/data-model`. Ajouter un fichier
 *    dans `packages/data-model/src/` fausserait le COMPTE DE FAMILLES cite par
 *    AGENTS.md section 7 (le decompte se fait par `ls` sur ce repertoire), et
 *    ferait croire a une entite metier de plus, avec ses types, ses vues et son
 *    repository generique. Le branding n'est rien de tout ca : c'est un
 *    reglage d'environnement, un document unique a identifiant fixe.
 *
 * 2. PAS DE `createdAt` / `createdBy`, donc pas de `hasRequiredStandardFields`
 *    dans les rules. Ces deux champs servent a tracer QUI a fait naitre une
 *    fiche parmi des milliers ; ici il n'y a qu'un document par org, et la
 *    seule question qu'on se pose devant lui est « qui a change ca, et quand »,
 *    a laquelle `updatedAt` / `updatedBy` repondent seuls. Pas de `archivedAt`
 *    ni de `deletedAt` non plus : un reglage ne s'archive pas, il se REMET A
 *    ZERO (`reinitialiser`, une vraie suppression), et l'environnement retombe
 *    alors sur le `taki.config.json` de deploiement.
 *
 * 3. LES IMAGES SONT STOCKEES EN BASE, en data URL, pas dans un bucket : le
 *    socle n'a pas Firebase Storage (ni rules, ni SDK). C'est un choix assume,
 *    borne par une taille maximale ecrite des deux cotes (ici et dans les
 *    rules), et DEUX bornes differentes : un logo est un lockup large, une
 *    icone est une marque carree affichee a vingt pixels.
 *
 * 4. LE DOCUMENT EST LISIBLE SANS AUTHENTIFICATION. Le nom, le logo, l'icone et
 *    la couleur s'affichent sur l'ecran de CONNEXION, donc a des visiteurs qui
 *    n'ont pas encore de compte. Il ne contient rien de secret, et l'`orgId`
 *    qui le designe est deja public (il vit dans `taki.config.json`, servi par
 *    l'hote). Cf. le commentaire du bloc de rules, c'est la seule lecture
 *    publique du depot.
 */

/** Le document unique du branding : `orgs/{orgId}/settings/branding`. */
const SETTINGS_COLLECTION = 'settings';
const BRANDING_DOCUMENT_ID = 'branding';

/** Version de schema du document (independante de `CURRENT_SCHEMA_VERSION`, ce n'est pas une famille). */
const BRANDING_SCHEMA_VERSION = 1;

/**
 * Taille maximale du logo, EN CARACTERES de la data URL. Un document Firestore
 * plafonne a 1 Mio : au-dela de cette borne, une image un peu grosse ferait
 * echouer l'ecriture avec une erreur de quota, loin de sa cause. La MEME valeur
 * est ecrite dans les rules : les deux doivent bouger ensemble.
 */
const LOGO_MAX_CARACTERES = 300000;

/**
 * Taille maximale de l'ICONE, plus serree que celle du logo, et pour une raison
 * de fond : l'icone s'affiche a vingt pixels au-dessus du dock et a trente-deux
 * dans l'onglet du navigateur. Au-dela de cent-vingt-huit pixels de cote, on ne
 * paie plus que du poids, dans un document lu par TOUS les visiteurs, connectes
 * ou non. La MEME valeur est ecrite dans les rules.
 */
const ICONE_MAX_CARACTERES = 100000;

/**
 * Les formes d'image acceptees (logo comme icone), et rien d'autre : trois
 * formats matriciels en
 * base64, plus le SVG (`;base64,`, `;charset=...,` ou `,` nu selon l'outil qui
 * base64, plus le SVG (`;base64,`, `;charset=...,` ou `,` nu selon l'outil qui
 * l'a produit). Le meme motif est ecrit en RE2 dans les rules. Interdire tout
 * le reste ferme la porte aux `data:text/html` et `javascript:` qu'une image
 * affichee en `<img src>` n'aurait aucune raison de porter.
 */
const MOTIF_IMAGE =
  /^(data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}|data:image\/svg\+xml[;,][\s\S]+)$/;

/**
 * La couleur d'accent : un hex a six chiffres en MINUSCULES, jamais trois, ni
 * un nom CSS. La casse est imposee parce que la valeur est COMPAREE (a la
 * couleur par defaut, entre le brouillon et l'enregistre) et derivee en
 * variantes : deux ecritures de la meme couleur feraient deux verites.
 */
const MOTIF_ACCENT = /^#[0-9a-f]{6}$/;

const nomSchema = z
  .string()
  .trim()
  .min(1, "le nom ne peut pas etre vide (absent ou `null` pour revenir au nom de la config)")
  .max(80, 'le nom ne peut pas depasser 80 caracteres');

/** Le schema d'une image du document : meme forme, borne propre a chaque role. */
function imageSchema(quoi: string, maxCaracteres: number) {
  return z
    .string()
    .max(
      maxCaracteres,
      `${quoi} ne peut pas depasser ${maxCaracteres} caracteres (data URL) : reduisez l'image avant de l'envoyer`,
    )
    .regex(
      MOTIF_IMAGE,
      `${quoi} doit etre une data URL d'image (\`data:image/png;base64,...\`, jpeg, webp ou \`data:image/svg+xml...\`)`,
    );
}

const logoSchema = imageSchema('le logo', LOGO_MAX_CARACTERES);
const iconeSchema = imageSchema("l'icone", ICONE_MAX_CARACTERES);

const accentSchema = z
  .string()
  .regex(
    MOTIF_ACCENT,
    "la couleur d'accent doit etre un hex a six chiffres en MINUSCULES, par exemple `#cbfb45`",
  );

/**
 * LE DOCUMENT, tel que les Security Rules l'exigent : memes bornes, meme
 * vocabulaire ferme. `updatedAt` et `updatedBy` n'y figurent pas : le SDK les
 * pose lui-meme, et `updatedAt` part en `serverTimestamp()`, une sentinelle
 * qu'aucun schema ne peut controler avant que le serveur ne la resolve (ce sont
 * les rules qui verifient, elles, que l'une vaut `request.time` et l'autre
 * l'uid de l'appelant).
 *
 * NON STRICT VOLONTAIREMENT : a la RELECTURE, une clef inconnue posee par une
 * version future est ignoree plutot que fatale. A l'ECRITURE, c'est le patch
 * qui est refuse (`brandingPatchSchema`, strict), et les rules refusent de
 * toute facon toute clef hors de la liste.
 */
export const brandingDocumentSchema = z.object({
  orgId: z.string().min(1),
  schemaVersion: z.number().int().positive(),
  name: nomSchema.optional(),
  logo: logoSchema.optional(),
  icon: iconeSchema.optional(),
  accent: accentSchema.optional(),
});

/**
 * LE PATCH, ce que l'ecran Parametres envoie. STRICT : une clef inconnue est
 * refusee ici plutot que par un `permission-denied` opaque venu des rules.
 * `null` y est une valeur legitime, et c'est le geste « retirer ma
 * personnalisation de ce champ » (cf. `enregistrer`).
 */
const brandingPatchSchema = z
  .object({
    name: nomSchema.nullable().optional(),
    logo: logoSchema.nullable().optional(),
    icon: iconeSchema.nullable().optional(),
    accent: accentSchema.nullable().optional(),
  })
  .strict();

/**
 * Ce que le client a choisi, champ par champ. Un champ ABSENT ou `null` veut
 * dire « pas personnalise » : l'hote retombe alors sur le `taki.config.json` de
 * deploiement. Le meme type est decrit cote interface dans
 * `packages/ui/src/lib/branding.tsx` (`BrandingPersonnalise`), qui ne parle
 * jamais au SDK.
 */
export interface BrandingPersonnalise {
  name?: string | null;
  logo?: string | null;
  icon?: string | null;
  accent?: string | null;
}

export interface BrandingRepository {
  /** Lit une fois ; `null` si le document n'existe pas (rien n'a jamais ete personnalise). */
  lire(): Promise<BrandingPersonnalise | null>;
  /**
   * Suit le document en direct : rappelle a chaque changement, `null` quand il
   * n'existe pas ou plus. Rend le DESABONNEMENT, a appeler au demontage.
   *
   * Pourquoi un abonnement et pas une lecture : deux onglets ouverts, ou un
   * admin qui change la couleur pendant qu'un collegue travaille, doivent voir
   * la meme chose sans recharger.
   */
  observer(
    surChangement: (branding: BrandingPersonnalise | null) => void,
    surErreur?: (erreur: unknown) => void,
  ): () => void;
  /**
   * Enregistre la personnalisation, champ par champ :
   *   - champ ABSENT du patch : laisse tel quel en base ;
   *   - champ a `null` : EFFACE du document (`deleteField()`), donc retombe sur
   *     le repli de la config ;
   *   - champ renseigne : valide puis ecrit.
   * Pose `orgId`, `schemaVersion`, `updatedAt` et `updatedBy` a chaque fois.
   * Valide AVANT d'ecrire, avec les memes bornes que les rules : une valeur
   * refusee leve ici, avec un message lisible, au lieu de revenir en
   * `permission-denied`.
   */
  enregistrer(patch: BrandingPersonnalise): Promise<void>;
  /** Efface toute la personnalisation : l'environnement revient a sa config de deploiement. */
  reinitialiser(): Promise<void>;
}

/**
 * Ce qu'on retient d'un document lu, CHAMP PAR CHAMP : une valeur qui ne passe
 * pas son schema est IGNOREE, pas fatale.
 *
 * Ce n'est pas de la mollesse, c'est le prix de la lecture publique : ce
 * document s'affiche sur l'ecran de CONNEXION, avant tout compte. Une valeur
 * abimee (posee par l'Admin SDK, ou par une version plus recente du schema)
 * doit faire retomber SON champ sur le repli de `taki.config.json`, jamais
 * planter l'ecran d'entree de tout le monde. A l'ECRITURE, rien ne passe :
 * `enregistrer` refuse le patch avant qu'il n'atteigne Firestore.
 */
function personnaliseDepuis(data: DocumentData): BrandingPersonnalise {
  const champ = (schema: z.ZodTypeAny, valeur: unknown): string | undefined => {
    const verdict = schema.safeParse(valeur);
    return verdict.success ? (verdict.data as string | undefined) : undefined;
  };
  return {
    name: champ(brandingDocumentSchema.shape.name, data['name']),
    logo: champ(brandingDocumentSchema.shape.logo, data['logo']),
    icon: champ(brandingDocumentSchema.shape.icon, data['icon']),
    accent: champ(brandingDocumentSchema.shape.accent, data['accent']),
  };
}

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`), comme pour les autres repositories : les
 *   rules exigent `updatedBy == request.auth.uid`, et un uid devine cote client
 *   ferait echouer l'ecriture sans rien expliquer.
 * @param orgId - l'org de la config chargee au runtime. Elle est dans le
 *   CHEMIN, pas seulement dans un champ (comme `orgs/{orgId}/members`) : une
 *   ecriture ne peut structurellement pas viser une autre org.
 */
export function createBrandingRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): BrandingRepository {
  const reference = doc(db, 'orgs', orgId, SETTINGS_COLLECTION, BRANDING_DOCUMENT_ID);

  return {
    async lire(): Promise<BrandingPersonnalise | null> {
      const snap = await getDoc(reference);
      if (!snap.exists()) return null;
      return personnaliseDepuis(snap.data());
    },

    observer(
      surChangement: (branding: BrandingPersonnalise | null) => void,
      surErreur?: (erreur: unknown) => void,
    ): () => void {
      return onSnapshot(
        reference,
        (snap) => {
          surChangement(snap.exists() ? personnaliseDepuis(snap.data()) : null);
        },
        (erreur) => {
          // Sans rappel d'erreur, une coupure reseau ou un refus des rules
          // laisserait l'appelant en attente pour toujours : on le lui dit, et
          // c'est a lui de garder le repli de la config a l'ecran.
          surErreur?.(erreur);
        },
      );
    },

    async enregistrer(patch: BrandingPersonnalise): Promise<void> {
      const verdict = brandingPatchSchema.safeParse(patch);
      if (!verdict.success) {
        const details = verdict.error.issues
          .map((probleme) => `${probleme.path.join('.') || '(racine)'} : ${probleme.message}`)
          .join(' ; ');
        throw new Error(
          `branding : personnalisation refusee avant ecriture (${details}). Ces bornes sont ` +
            'exactement celles des Security Rules : les contourner ici ne ferait que remplacer ' +
            'un message clair par un `permission-denied`.',
        );
      }
      const fields = verdict.data;

      // `setDoc(..., { merge: true })` n'ecrit QUE les clefs presentes : un
      // champ absent du patch reste tel quel en base, un champ a `null` part en
      // `deleteField()` et disparait du document. C'est ce qui permet a l'ecran
      // Parametres de ne toucher qu'au reglage qu'on vient de modifier.
      const data: DocumentData = {
        orgId,
        schemaVersion: BRANDING_SCHEMA_VERSION,
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      };
      for (const clef of ['name', 'logo', 'icon', 'accent'] as const) {
        const valeur = fields[clef];
        if (valeur === undefined) continue;
        data[clef] = valeur === null ? deleteField() : valeur;
      }

      await setDoc(reference, data, { merge: true });
    },

    async reinitialiser(): Promise<void> {
      // LA SEULE SUPPRESSION DURE DU SDK, et elle est assumee : partout
      // ailleurs on archive (`archive-ops.ts`) parce qu'une fiche metier a une
      // histoire. Un reglage n'en a pas, et « remettre a zero » doit
      // rendre l'environnement STRICTEMENT identique a ce qu'il etait avant
      // toute personnalisation, document compris.
      await deleteDoc(reference);
    },
  };
}
